import { useState, useRef, useEffect } from 'react'
import { useDocumentTitle } from '../hooks/useDocumentTitle'
import { useNavigate } from 'react-router-dom'
import { Search, ArrowLeft, ChevronRight, Loader2, ChevronDown, Check } from 'lucide-react'
import { useApi, useAsanaApi } from '../hooks/useApi'
import { useData } from '../hooks/useData'

import { generateId, generateToken, formatDate } from '../lib/utils'
import { TIERS } from '../lib/constants'
import Field, { inputClass } from '../components/Field'
import Select from '../components/Select'
import DateInput from '../components/DateInput'
import LoadingSpinner from '../components/LoadingSpinner'
import Modal from '../components/Modal'
import ProductPicker from '../components/ProductPicker'
import Popover from '../components/Popover'
import type { Settings, AsanaProject, AsanaCustomField, Product, AdoptionMode } from '../types'

// Adoption mode options shared between AddProject and ProjectDetail.
// Keep this in sync with `AdoptionMode` in types/index.ts and USER_GUIDE §6.5.
const ADOPTION_MODE_OPTIONS = [
  ['users',   'Users (count)'],
  ['percent', 'Direct %'],
  ['na',      'Not applicable'],
] as const

// Dropdown used only in the Asana field-mapping step.
// Each option shows the Asana field name + a coloured value tag.
// Options with no Asana value show a grey "Empty" tag but remain selectable so mappings persist for future updates.
type AsanaMappingOption = { val: string; name: string; asanaValue: string | null; typeDisabled?: boolean }

function AsanaMappingSelect({ value, onChange, options, disabled }: {
  value: string; onChange: (v: string) => void
  options: AsanaMappingOption[]; disabled?: boolean
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const selected = options.find(o => o.val === value)

  return (
    <div ref={ref} className="relative flex-1">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => !disabled && setOpen(!open)}
        onKeyDown={e => {
          if (!disabled && !open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            e.preventDefault()
            setOpen(true)
          }
        }}
        className={`w-full flex items-center justify-between gap-2 border rounded-lg px-3 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-danone-cyan ${
          disabled
            ? 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
            : 'border-slate-200 bg-white text-slate-700 cursor-pointer hover:border-slate-300'
        }`}
      >
        {selected ? (
          <span className="flex items-center gap-2 min-w-0 overflow-hidden">
            <span className="truncate shrink">{selected.name}</span>
            {selected.val !== '' && (
              selected.asanaValue
                ? <span className="shrink-[2] min-w-0 text-xs font-mono px-1.5 py-0.5 rounded border truncate" style={{ background: 'rgba(239,51,64,0.08)', color: '#EF3340', borderColor: 'rgba(239,51,64,0.25)' }}>{selected.asanaValue}</span>
                : <span className="flex-shrink-0 text-xs font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-400 border border-slate-200">Empty</span>
            )}
          </span>
        ) : (
          <span className="text-slate-400">Select...</span>
        )}
        <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      <Popover open={open && !disabled} anchorRef={ref} onClose={() => setOpen(false)}>
        <div role="listbox" className="py-1 min-h-0 overflow-y-auto">
          {options.map(opt => {
            const isSelected = value === opt.val
                        // When an Asana custom field currently has no value we still allow mapping
                        // so future Asana updates will flow through. Keep type-based disabling.
                        const isDisabled = !!opt.typeDisabled
                        return (
                          <button
                            key={opt.val}
                            type="button"
                            role="option"
                            aria-selected={isSelected}
                            data-option
                            disabled={isDisabled}
                            onClick={() => { if (!isDisabled) { onChange(opt.val); setOpen(false) } }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors focus:outline-none focus-visible:bg-slate-100 ${
                  isDisabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-slate-50 text-slate-700'
                }`}
              >
                <span className={`w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-danone-cyan border-danone-cyan' : 'border-slate-300'}`}>
                  {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                </span>
                <span className="flex items-center gap-2 min-w-0 overflow-hidden">
                  <span className="truncate shrink">{opt.name}</span>
                  {opt.val !== '' && (
                    opt.asanaValue
                      ? <span className="shrink-[2] min-w-0 text-xs font-mono px-1.5 py-0.5 rounded border truncate" style={{ background: 'rgba(239,51,64,0.08)', color: '#EF3340', borderColor: 'rgba(239,51,64,0.25)' }}>{opt.asanaValue}</span>
                      : <span className="flex-shrink-0 text-xs font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-400 border border-slate-200">Empty</span>
                  )}
                </span>
              </button>
            )
          })}
        </div>
      </Popover>
    </div>
  )
}

type Step = 'choose' | 'asana-search' | 'asana-map' | 'manual'

export default function AddProject() {
  useDocumentTitle('New Project')
  const api = useApi()
  const asanaApi = useAsanaApi()
  const navigate = useNavigate()

  // Fetch settings + products in parallel so the manual form can offer a
  // Product dropdown without a second waterfall on submit.
  const { data, loading } = useData(() =>
    Promise.all([api.getSettings(), api.getProducts()]).then(([settings, products]) => ({ settings, products }))
  )
  const settings = (data as { settings: Settings; products: Product[] } | null)?.settings
  const products = (data as { settings: Settings; products: Product[] } | null)?.products || []
  const [step, setStep] = useState<Step>('choose')
  const [asanaConnected, setAsanaConnected] = useState(false)

  useEffect(() => {
    asanaApi.searchProjects('test').then(() => setAsanaConnected(true)).catch(() => setAsanaConnected(false))
  }, [])

  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<AsanaProject[]>([])
  const [selectedProject, setSelectedProject] = useState<AsanaProject | null>(null)
  const [customFields, setCustomFields] = useState<AsanaCustomField[]>([])
  const [fieldMappings, setFieldMappings] = useState<Record<string, string | null>>({
    dueDate: 'due_on', actualGoLive: null, forecastCost: null, actualCost: null,
  })
  const [searching, setSearching] = useState(false)
  const [errorModal, setErrorModal] = useState<string | null>(null)
  const [manualData, setManualData] = useState({
    name: '', description: '', subPortfolio: '', tier: '', dueDate: '', forecastCost: 0, expectedValue: 0, expectedUsers: 0,
    productId: '', team: '', adoptionMode: 'users' as AdoptionMode, skipStakeholders: false,
  })
  // Sub-portfolio, tier, Delivered in Full, and Product are always set manually -
  // they can't reliably be mapped from arbitrary Asana custom fields.
  const [asanaDefaults, setAsanaDefaults] = useState({ subPortfolio: '', tier: '', deliveredInFull: '' as 'Y' | 'N' | '', productId: '', team: '' })

  const handleAsanaSearch = async () => {
    if (!searchQuery) return
    setSearching(true)
    try {
      setSearchResults(await asanaApi.searchProjects(searchQuery))
    } catch { setErrorModal('Failed to search Asana. Make sure you are signed in via Settings.') }
    setSearching(false)
  }

  const handleSelectProject = async (project: AsanaProject) => {
    setSelectedProject(project)
    try {
      const full = await asanaApi.getProject(project.gid)
      setCustomFields(full.custom_fields || [])
      setStep('asana-map')
    } catch {}
  }

  const handleImportAsana = async () => {
    if (!selectedProject) return
    // Note: do NOT include `id` here. The backend assigns the canonical
    // `proj_<uuid>` id; we navigate using the returned project's id below.
    const newProject: any = {
      name: selectedProject.name, asanaProjectId: selectedProject.gid,
      subPortfolio: asanaDefaults.subPortfolio || settings?.subPortfolios?.[0] || '',
      tier: asanaDefaults.tier,
      deliveredInFull: asanaDefaults.deliveredInFull || undefined,
      productId: asanaDefaults.productId || undefined,
      team: asanaDefaults.team || undefined,
      status: 'forecast' as const,
      dueDate: selectedProject.due_on || new Date().toISOString().split('T')[0],
      forecastCost: 0, expectedValue: 0, expectedUsers: 0,
      adoptionMode: 'users' as const,
      stakeholders: [],
      surveyToken: generateToken(),
      lastSyncedAt: new Date().toISOString(),
    }

    for (const [field, gid] of Object.entries(fieldMappings)) {
      if (!gid || gid === 'due_on') continue
      const cf = customFields.find(c => c.gid === gid)
      if (cf) {
        if (cf.type === 'number' && cf.number_value !== undefined && cf.number_value !== null) newProject[field] = cf.number_value
        else if (cf.type === 'date') newProject[field] = cf.date_value?.date ?? null
        else if (cf.enum_value) newProject[field] = cf.enum_value.name
        else if (cf.display_value) newProject[field] = cf.display_value
      }
    }

    const created = await api.createProject(newProject)
    await api.saveFieldMapping({ id: generateId(), projectId: created.id, asanaProjectId: selectedProject.gid, mappings: fieldMappings })

    // Check if Asana project uses the PD template (fire-and-forget)
    asanaApi.checkTemplate(selectedProject.gid).then(result => {
      api.updateProject(created.id, { templateMatch: result.templateMatch, templateCheckedAt: new Date().toISOString() })
    }).catch(() => {})

    navigate(`/projects/${created.id}`)
  }

  const handleCreateManual = async () => {
    const { productId, team, description, skipStakeholders, ...rest } = manualData
    // Backend assigns the canonical `proj_<uuid>` id; navigate using the
    // returned project so we never race a doc that hasn't been written yet.
    // `skipStakeholders` only goes on the wire when true so we don't pollute
    // projects the user never opted out of.
    const newProject = {
      ...rest, status: 'forecast' as const,
      productId: productId || undefined,
      team: team || undefined,
      description: description.trim() || undefined,
      stakeholders: [],
      surveyToken: generateToken(),
      ...(skipStakeholders ? { skipStakeholders: true } : {}),
    }
    const created = await api.createProject(newProject)
    navigate(`/projects/${created.id}`)
  }

  if (loading) return <LoadingSpinner />

  return (
    <div className="max-w-3xl mx-auto">
      <Modal open={!!errorModal} title="Error" message={errorModal ?? ''} variant="danger" onConfirm={() => setErrorModal(null)} />
      <button onClick={() => navigate('/projects')} className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to Projects
      </button>
      <h1 className="text-2xl font-bold text-slate-800 mb-2">Add Project</h1>
      <p className="text-slate-500 mb-8">Import from Asana or create manually</p>

      {step === 'choose' && (
        <div className="grid grid-cols-2 gap-4">
          <button onClick={() => asanaConnected && setStep('asana-search')} disabled={!asanaConnected}
            className={`card p-6 text-left transition-all ${asanaConnected ? 'hover:border-danone-cyan hover:shadow-md cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}>
            <div className="flex items-center gap-3 mb-3">
              <img src="https://cdn.jsdelivr.net/gh/homarr-labs/dashboard-icons/svg/asana.svg" alt="Asana" className="w-8 h-8" />
              <h3 className={`font-bold ${asanaConnected ? 'text-slate-800' : 'text-slate-400'}`}>Import from Asana</h3>
            </div>
            <p className={`text-sm mb-4 ${asanaConnected ? 'text-slate-600' : 'text-slate-400'}`}>Search and select a project from Asana. Map fields to TrackIT.</p>
            {!asanaConnected && <p className="text-xs text-amber-600">Connect Asana in Settings first</p>}
            <ChevronRight className="w-5 h-5 text-slate-400 ml-auto" />
          </button>
          <button onClick={() => setStep('manual')} className="card p-6 text-left hover:border-danone-cyan hover:shadow-md transition-all">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center"><span className="text-lg">📝</span></div>
              <h3 className="font-bold text-slate-800">Create Manually</h3>
            </div>
            <p className="text-sm text-slate-600 mb-4">Enter project details manually. For projects not in Asana.</p>
            <ChevronRight className="w-5 h-5 text-slate-400 ml-auto" />
          </button>
        </div>
      )}

      {step === 'asana-search' && (
        <div className="card p-6">
          <div className="flex items-center gap-3 mb-6">
            <img src="https://cdn.jsdelivr.net/gh/homarr-labs/dashboard-icons/svg/asana.svg" alt="Asana" className="w-7 h-7" />
            <h2 className="font-bold text-slate-800">Search Asana Projects</h2>
          </div>
          <div className="flex gap-2 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input type="text" placeholder="Search by project name..." value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAsanaSearch()}
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-danone-cyan" />
            </div>
            <button onClick={handleAsanaSearch} disabled={searching || !searchQuery} className="btn-primary flex items-center gap-2">
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {searching ? 'Searching...' : 'Search'}
            </button>
          </div>
          {searchResults.length > 0 && (
            <div className="space-y-2">
              {searchResults.map(p => (
                <button key={p.gid} onClick={() => handleSelectProject(p)} className="w-full p-4 bg-slate-50 rounded-lg text-left hover:bg-slate-100 transition-colors">
                  <div className="font-medium text-slate-800">{p.name}</div>
                  {p.due_on && <div className="text-xs text-slate-500 mt-1">Due: {formatDate(p.due_on)}</div>}
                </button>
              ))}
            </div>
          )}
          <div className="flex gap-2 mt-6">
            <button onClick={() => setStep('choose')} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800">← Back</button>
          </div>
        </div>
      )}

      {step === 'asana-map' && selectedProject && (
        <div className="card p-6">
          <h2 className="font-bold text-slate-800 mb-2">Map Fields</h2>
          <p className="text-sm text-slate-500 mb-6">Importing: <strong>{selectedProject.name}</strong></p>

          {/* Asana field mapping - maps Asana custom fields to KPI tracker fields.
              Disabled (greyed out) if Asana has no custom fields to offer. */}
          <div className="space-y-3 mb-6">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Asana Field Mapping</p>
            {[
              { key: 'dueDate', label: 'Due Date', hasDefault: true, fieldType: 'date' },
              { key: 'actualGoLive', label: 'Actual Go-Live', fieldType: 'date' },
              { key: 'forecastCost', label: 'Forecast Cost', fieldType: 'number' },
              { key: 'actualCost', label: 'Actual Cost', fieldType: 'number' },
            ].map(f => {
              // Build structured options so AsanaMappingSelect can render value tags.
              // For currency fields, format the number with the currency symbol.
              // Type-incompatible options are flagged as typeDisabled and sorted to the bottom.
              const cfOptions: AsanaMappingOption[] = customFields.map(cf => {
                let asanaValue: string | null = cf.display_value ?? null
                if (cf.type === 'number' && cf.number_value !== undefined && cf.number_value !== null) {
                  asanaValue = cf.format === 'currency' && cf.currency_code
                    ? new Intl.NumberFormat('en-GB', { style: 'currency', currency: cf.currency_code }).format(cf.number_value)
                    : String(cf.number_value)
                } else if (cf.type === 'date' && cf.display_value) {
                  asanaValue = formatDate(cf.display_value)
                }
                const typeDisabled = cf.type !== f.fieldType
                return { val: cf.gid, name: cf.name, asanaValue, typeDisabled }
              })
              // Compatible fields first, type-incompatible at the bottom
              const sorted = [...cfOptions.filter(o => !o.typeDisabled), ...cfOptions.filter(o => o.typeDisabled)]
              const options: AsanaMappingOption[] = [
                { val: '', name: 'Skip / enter manually', asanaValue: null },
                ...(f.hasDefault ? [{ val: 'due_on', name: 'Due Date', asanaValue: selectedProject.due_on ? formatDate(selectedProject.due_on) : null }] : []),
                ...sorted,
              ]
              const noFields = customFields.length === 0 && !f.hasDefault
              return (
                <div key={f.key} className="flex items-center gap-3">
                  <div className="w-36 text-sm font-medium text-slate-700 flex-shrink-0">{f.label}</div>
                  {noFields ? (
                    <div className="flex-1 border border-slate-200 bg-slate-50 rounded-lg px-3 py-2 text-sm text-slate-400">No Asana fields available</div>
                  ) : (
                    <AsanaMappingSelect
                      value={fieldMappings[f.key] || ''}
                      onChange={v => setFieldMappings({ ...fieldMappings, [f.key]: v || null })}
                      options={options}
                    />
                  )}
                </div>
              )
            })}
          </div>

          {/* These fields are always set manually - Sub-Portfolio uses DB options, Tier uses the standard list */}
          <div className="space-y-3 pt-5 border-t border-slate-100">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Set Manually</p>
            <div className="flex items-center gap-4">
              <div className="w-36 text-sm font-medium text-slate-700 flex-shrink-0">Sub-Portfolio</div>
              <Select value={asanaDefaults.subPortfolio} onChange={v => setAsanaDefaults({ ...asanaDefaults, subPortfolio: v })} options={settings?.subPortfolios ?? []} placeholder="Select..." className="flex-1" />
            </div>
            <div className="flex items-center gap-4">
              <div className="w-36 text-sm font-medium text-slate-700 flex-shrink-0">Tier</div>
              <Select value={asanaDefaults.tier} onChange={v => setAsanaDefaults({ ...asanaDefaults, tier: v })} options={TIERS} placeholder="Select..." className="flex-1" />
            </div>
            <div className="flex items-center gap-4">
              <div className="w-36 text-sm font-medium text-slate-700 flex-shrink-0">Team</div>
              <Select value={asanaDefaults.team} onChange={v => setAsanaDefaults({ ...asanaDefaults, team: v })} options={settings?.teams ?? []} placeholder="No team" className="flex-1" />
            </div>
            <div className="flex items-center gap-4">
              <div className="w-36 text-sm font-medium text-slate-700 flex-shrink-0">Delivered in Full</div>
              <Select value={asanaDefaults.deliveredInFull} onChange={v => setAsanaDefaults({ ...asanaDefaults, deliveredInFull: v as 'Y' | 'N' | '' })} options={[['Y', 'Yes'], ['N', 'No']]} placeholder="Not set" className="flex-1" />
            </div>
            <div className="flex items-center gap-4">
              <div className="w-36 text-sm font-medium text-slate-700 flex-shrink-0">Product</div>
              <div className="flex-1">
                <ProductPicker
                  value={asanaDefaults.productId || undefined}
                  onChange={v => setAsanaDefaults({ ...asanaDefaults, productId: v || '' })}
                  products={products}
                />
              </div>
            </div>
          </div>

          <div className="flex gap-2 mt-8">
            <button onClick={() => setStep('asana-search')} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800">← Back</button>
            <button onClick={handleImportAsana} className="btn-primary ml-auto">Import Project</button>
          </div>
        </div>
      )}

      {step === 'manual' && (
        <div className="card p-6">
          <h2 className="font-bold text-slate-800 mb-6">Create Project Manually</h2>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Project Name" className="col-span-2">
              <input type="text" value={manualData.name} onChange={e => setManualData({ ...manualData, name: e.target.value })} className={inputClass} placeholder="Enter project name" />
            </Field>
            <Field label="Description" className="col-span-2">
              <textarea
                value={manualData.description}
                onChange={e => setManualData({ ...manualData, description: e.target.value })}
                className={`${inputClass} min-h-[72px] resize-y`}
                placeholder="What this initiative delivers (shown on the Scorecard)"
              />
            </Field>
            <Field label="Sub-Portfolio" needed={!manualData.subPortfolio}>
              <Select value={manualData.subPortfolio} onChange={v => setManualData({ ...manualData, subPortfolio: v })} options={settings?.subPortfolios ?? []} placeholder="Select..." className="w-full" />
            </Field>
            <Field label="Tier" needed={!manualData.tier}>
              <Select value={manualData.tier} onChange={v => setManualData({ ...manualData, tier: v })} options={TIERS} placeholder="Select..." className="w-full" />
            </Field>
            <Field label="Team">
              <Select value={manualData.team} onChange={v => setManualData({ ...manualData, team: v })} options={settings?.teams ?? []} placeholder="No team" className="w-full" />
            </Field>
            <Field label="Due Date" needed={!manualData.dueDate}><DateInput value={manualData.dueDate} onChange={v => setManualData({ ...manualData, dueDate: v })} /></Field>
            <Field label="Forecast Cost (€)"><input type="number" value={manualData.forecastCost} onChange={e => setManualData({ ...manualData, forecastCost: Number(e.target.value) })} className={inputClass} /></Field>
            <Field label="Expected Value (Annual Hrs)"><input type="number" value={manualData.expectedValue} onChange={e => setManualData({ ...manualData, expectedValue: Number(e.target.value) })} className={inputClass} /></Field>
            <Field label="Adoption Measured By">
              <Select
                value={manualData.adoptionMode}
                onChange={v => setManualData({ ...manualData, adoptionMode: v as AdoptionMode })}
                options={ADOPTION_MODE_OPTIONS}
                className="w-full"
              />
            </Field>
            {manualData.adoptionMode !== 'na' && (
              <Field label="Expected Users"><input type="number" value={manualData.expectedUsers} onChange={e => setManualData({ ...manualData, expectedUsers: Number(e.target.value) })} className={inputClass} placeholder="Total user base" /></Field>
            )}
            <Field label="Product" className="col-span-2">
              <ProductPicker
                value={manualData.productId || undefined}
                onChange={v => setManualData({ ...manualData, productId: v || '' })}
                products={products}
              />
            </Field>
            {/* Opt-out for back-loaded / legacy projects. When ticked the project
                skips the survey flow entirely: no stakeholders list, no Send
                Survey, no Responses step, and Satisfaction KPI excludes it. */}
            <div className="col-span-2">
              <label className="flex items-start gap-2.5 p-3 rounded-lg border border-slate-200 bg-slate-50/50 hover:bg-slate-50 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="w-3.5 h-3.5 mt-0.5 rounded accent-slate-500"
                  checked={manualData.skipStakeholders}
                  onChange={e => setManualData({ ...manualData, skipStakeholders: e.target.checked })}
                />
                <span className="text-sm">
                  <span className="font-medium text-slate-700">No stakeholders / skip survey</span>
                  <span className="block text-[11px] text-slate-500 mt-0.5">For back-loaded or legacy projects. No survey flow, no "needs configuration" nag. Satisfaction KPI excludes this project.</span>
                </span>
              </label>
            </div>
          </div>
          <div className="flex gap-2 mt-8">
            <button onClick={() => setStep('choose')} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800">← Back</button>
            <button onClick={handleCreateManual} disabled={!manualData.name || !manualData.dueDate || !manualData.subPortfolio || !manualData.tier} className="btn-primary ml-auto disabled:opacity-50">Create Project</button>
          </div>
        </div>
      )}
    </div>
  )
}
